import type {
  LfaAccountServiceV2FieldsFragment,
  LfaServicesV2FieldsFragment,
} from '~/graphql/types';
import type { OfficeChangeFragment } from './types';
import { locationsEqual, calendarRefsEqual } from './comparisons';

type ResolvedService = Omit<LfaAccountServiceV2FieldsFragment, '__typename'>;

const computeOfficeChangeset = (
  accountServices: LfaServicesV2FieldsFragment['accountServices'],
  desiredServices: Array<ResolvedService>,
): Array<OfficeChangeFragment> => {
  const changes: Array<OfficeChangeFragment> = [];

  const accountMap = new Map(accountServices.map(s => [s.id, s]));
  const desiredMap = new Map(desiredServices.map(s => [s.id, s]));

  for (const accountService of accountServices) {
    if (!desiredMap.has(accountService.id)) {
      changes.push({
        __typename: 'LFA_Office_Change_Remove_V2',
        serviceId: accountService.id,
      });
    }
  }

  for (const desired of desiredServices) {
    const account = accountMap.get(desired.id);

    if (!account) {
      changes.push({
        __typename: 'LFA_Office_Change_Add_V2',
        serviceId: desired.id,
        addLocations: desired.locations,
        addCriteria: desired.criteria,
        addCalendars: desired.calendars,
      });
      continue;
    }

    const hasLocationChanges = !locationsEqual(
      account.locations,
      desired.locations,
    );

    const accountCriteriaIds = new Set(account.criteria.map(c => c.id));
    const desiredCriteriaIds = new Set(desired.criteria.map(c => c.id));
    const addCriteria = desired.criteria.filter(
      c => !accountCriteriaIds.has(c.id),
    );
    const removeCriteria = account.criteria
      .filter(c => !desiredCriteriaIds.has(c.id))
      .map(c => c.id);

    const accountCalendarIds = new Set(
      account.calendars.map(c => c.calendarId),
    );
    const desiredCalendarIds = new Set(
      desired.calendars.map(c => c.calendarId),
    );
    const addCalendars = desired.calendars.filter(
      c => !accountCalendarIds.has(c.calendarId),
    );
    const removeCalendarIds = account.calendars
      .filter(c => !desiredCalendarIds.has(c.calendarId))
      .map(c => c.calendarId);

    const hasCalendarWeightChanges = !calendarRefsEqual(
      desired.calendars.filter(c => accountCalendarIds.has(c.calendarId)),
      account.calendars.filter(c => desiredCalendarIds.has(c.calendarId)),
    );

    const hasChanges =
      hasLocationChanges ||
      addCriteria.length > 0 ||
      removeCriteria.length > 0 ||
      addCalendars.length > 0 ||
      removeCalendarIds.length > 0 ||
      hasCalendarWeightChanges;

    if (hasChanges) {
      const mutateLocations = hasLocationChanges ? desired.locations : null;
      const effectiveAddCalendars =
        addCalendars.length > 0 || hasCalendarWeightChanges
          ? desired.calendars.filter(c => {
              const acctRef = account.calendars.find(
                ac => ac.calendarId === c.calendarId,
              );
              if (!acctRef) return true;
              return (
                c.weight !== acctRef.weight ||
                (c.note ?? '') !== (acctRef.note ?? '')
              );
            })
          : null;

      changes.push({
        __typename: 'LFA_Office_Change_Mutate_V2',
        serviceId: desired.id,
        mutateLocations,
        mutateAddCriteria: addCriteria.length > 0 ? addCriteria : null,
        removeCriteria: removeCriteria.length > 0 ? removeCriteria : null,
        mutateAddCalendars:
          effectiveAddCalendars && effectiveAddCalendars.length > 0
            ? effectiveAddCalendars
            : null,
        removeCalendarIds:
          removeCalendarIds.length > 0 ? removeCalendarIds : null,
      });
    }
  }

  return changes;
};

export default computeOfficeChangeset;
